import {paginatedApiRequest} from './api';
import {adminRequest, readableMessage} from './adminRequest';
import {
  adminSupportConversationsEndpoint,
  adminSupportMessagesEndpoint,
  adminSupportReadEndpoint,
  adminSupportResolveEndpoint,
} from '../constants/endpoints';

export interface SupportConversation {
  conversationId: string;
  userId: string;
  fullName: string;
  // Null when the account is gone from the API.
  email?: string | null;
  profilePhoto?: string | null;
  roleCode?: number | null;
  lastMessageAtUtc: string;
  lastMessagePreview?: string | null;
  lastMessageFromSupport: boolean;
  unreadCount: number;
  // Null while it's open; any new message reopens it.
  resolvedAtUtc?: string | null;
}

export interface SupportMessage {
  id: string;
  body: string;
  fromSupport: boolean;
  createdAtUtc: string;
}

export interface SupportConversationsPage {
  items: SupportConversation[];
  totalItems: number;
  maxPage: number;
}

export const fetchSupportConversations = async (
  pageNumber: number,
  pageSize: number,
  resolved: boolean,
): Promise<SupportConversationsPage> => {
  const failureMessage = 'Failed to fetch the support conversations';
  let failure: Error | null = null;

  const response = await paginatedApiRequest<SupportConversation[]>(
    {
      endpoint: adminSupportConversationsEndpoint,
      method: 'GET',
      queryParams: {pageNumber, pageSize, resolved},
    },
    {
      errorMessage: failureMessage,
      onError: (error: unknown) => {
        failure = new Error(readableMessage(error, failureMessage));
      },
    },
  );

  if (failure) {
    throw failure;
  }

  return {
    items: response?.data ?? [],
    totalItems: response?.totalItems ?? 0,
    maxPage: response?.maxPage ?? 0,
  };
};

export const fetchSupportMessages = async (
  conversationId: string,
  limit: number,
): Promise<SupportMessage[]> =>
  (await adminRequest<SupportMessage[]>(
    {
      endpoint: adminSupportMessagesEndpoint(conversationId),
      method: 'GET',
      queryParams: {limit},
    },
    'Failed to fetch the messages',
  )) ?? [];

export const sendSupportReply = async (
  conversationId: string,
  body: string,
): Promise<void> => {
  await adminRequest<null, {body: string}>(
    {
      endpoint: adminSupportMessagesEndpoint(conversationId),
      method: 'POST',
      body: {body},
    },
    'Failed to send the message',
  );
};

export const resolveSupportConversation = async (
  conversationId: string,
  body: string,
): Promise<void> => {
  await adminRequest<null, {body: string}>(
    {
      endpoint: adminSupportResolveEndpoint(conversationId),
      method: 'POST',
      body: {body},
    },
    'Failed to resolve the conversation',
  );
};

export const markSupportConversationRead = async (
  conversationId: string,
): Promise<void> => {
  await adminRequest<null>(
    {endpoint: adminSupportReadEndpoint(conversationId), method: 'POST'},
    'Failed to mark the conversation as read',
  );
};
